import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { palette } from '@livable/saas-react-components';
import { makeStyles } from '@mui/styles';
const border = "1px solid ".concat(palette.shades[300]);
const cardShadow = '0px 10px 12.5px rgba(184, 196, 206, 0.2)';
/** Room under the last card in the scrolling pane, so the end of the details clears the bottom edge. */
const DATA_CONTENT_BOTTOM_PADDING = 80;
/** The same room below a 1280px window, where the pane sits under the document instead of beside it. */
const DATA_CONTENT_BOTTOM_PADDING_NARROW = 40;
const tableText = {
  color: palette.shades[800],
  fontFamily: palette.fontFamilies.source,
  fontSize: '13px',
  lineHeight: 'normal'
};

/** Styles for the uploaded bill details view (Figma: Apply billing - default). */
const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'stretch',
    // The modal backdrop reserves 60px above and below, so this fills the rest of the viewport.
    height: 'calc(100vh - 120px)',
    overflow: 'hidden',
    backgroundColor: palette.shades[200],
    '@media (max-width: 1279px)': {
      flexDirection: 'column'
    },
    // The modal drops to a 10px backdrop gutter on small screens.
    '@media (max-width: 899px)': {
      height: 'calc(100vh - 20px)'
    }
  },
  filePane: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    justifyContent: 'center',
    // Wide documents scroll inside the column instead of stretching the modal.
    overflow: 'auto',
    padding: '32px 32px 12px',
    '@media (max-width: 1279px)': {
      flex: '0 0 40%',
      padding: '16px'
    }
  },
  filePaneInner: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '800px'
  },
  filePage: {
    flex: 1,
    minHeight: '480px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border,
    borderRadius: '12px',
    boxShadow: cardShadow,
    backgroundColor: palette.shades[100],
    // Stretch the shared file preview (fixed 584px tall by default) over the whole column.
    '& > div:first-child': {
      flex: 1,
      minHeight: 0,
      '& > div': {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minWidth: 0
      }
    },
    '& #react-doc-viewer': {
      flex: 1,
      minHeight: 0,
      height: 'auto !important'
    }
  },
  filePlaceholder: {
    padding: '24px',
    border,
    borderRadius: '12px',
    color: palette.shades[700],
    backgroundColor: palette.shades[100]
  },
  dataPane: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    borderLeft: border,
    backgroundColor: palette.shades[200],
    '@media (max-width: 1279px)': {
      borderLeft: 'none',
      borderTop: border
    }
  },
  dataHeader: {
    flexShrink: 0,
    padding: '32px',
    '@media (max-width: 1279px)': {
      padding: '16px'
    }
  },
  fileCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '12px 12px 12px 16px',
    borderRadius: '12px',
    border: "1px solid ".concat(palette.shades[500]),
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(8px)',
    boxShadow: cardShadow
  },
  fileCardTitle: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    '& .MuiSvgIcon-root': {
      flexShrink: 0,
      width: '24px',
      height: '24px',
      fontSize: '24px'
    }
  },
  fileCardName: {
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    color: palette.shades[800],
    fontFamily: palette.fontFamilies.poppins,
    fontSize: '15px',
    fontWeight: 600
  },
  iconButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px',
    border: 'none',
    borderRadius: '2px',
    cursor: 'pointer',
    backgroundColor: palette.shades[200],
    '& .MuiSvgIcon-root': {
      width: '24px',
      height: '24px',
      fontSize: '24px'
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'default'
    }
  },
  dataContent: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '32px',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: "16px 44px ".concat(DATA_CONTENT_BOTTOM_PADDING, "px"),
    // Every block keeps its natural height, otherwise the cards squeeze instead of the panel scrolling.
    '& > *': {
      flexShrink: 0,
      width: '100%',
      maxWidth: '720px'
    },
    '@media (max-width: 1279px)': {
      padding: "16px 16px ".concat(DATA_CONTENT_BOTTOM_PADDING_NARROW, "px")
    }
  },
  dataContentBlock: {
    width: '100%',
    maxWidth: '720px'
  },
  thinkingLine: {
    color: palette.shades[700],
    fontFamily: palette.fontFamilies.source,
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: 1.3
  },
  introLine: {
    color: palette.shades[800],
    fontFamily: palette.fontFamilies.source,
    fontSize: '16px',
    lineHeight: 1.3
  },
  planNeeds: {
    display: 'inline-flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '4px',
    fontSize: '14px'
  },
  // LB-6099: the panel's two parts, named a size above the cards' own titles (15px).
  sectionHeading: {
    borderBottom: border,
    paddingBottom: '8px'
  },
  sectionHeadingTitle: {
    margin: 0,
    color: palette.shades[800],
    fontFamily: palette.fontFamilies.poppins,
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: 1.3
  },
  sectionHeadingCaption: {
    margin: '4px 0 0',
    color: palette.shades[700],
    fontFamily: palette.fontFamilies.source,
    fontSize: '14px',
    lineHeight: 1.4
  },
  card: {
    width: '100%',
    maxWidth: '720px',
    overflow: 'hidden',
    border,
    borderRadius: '12px',
    backgroundColor: palette.shades[100]
  },
  cardTinted: {
    backgroundColor: '#FDFEFF'
  },
  /** Editing opens calendars that are taller than the rows they belong to, and they may hang out. */
  cardOverflowing: {
    overflow: 'visible'
  },
  /**
   * Clips to the rounded corners as `hidden` does, but is no scroll container: an allocation table's Allocate or
   * Approve can then stick to the bottom of the pane while its units scroll past (LB-6036). Under `hidden` it would
   * stick to this card, which never scrolls, and so never move.
   */
  cardClipped: {
    overflow: 'clip',
    // A sticky box stops as far above the pane's edge as the pane's own bottom padding, which left the table's button
    // floating over rows still scrolling beneath it. Letting it down by the padding pins it to the edge itself.
    '--allocation-actions-sticky-bottom': "-".concat(DATA_CONTENT_BOTTOM_PADDING, "px"),
    '@media (max-width: 1279px)': {
      '--allocation-actions-sticky-bottom': "-".concat(DATA_CONTENT_BOTTOM_PADDING_NARROW, "px")
    }
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    minHeight: '60px',
    padding: '12px 16px',
    borderBottom: border
  },
  cardHeaderTitles: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0
  },
  cardTitle: {
    color: palette.shades[800],
    fontFamily: palette.fontFamilies.poppins,
    fontSize: '15px',
    fontWeight: 600
  },
  statusChip: {
    flexShrink: 0,
    padding: '2px 6px',
    borderRadius: '2px',
    color: palette.shades[700],
    backgroundColor: palette.shades[300],
    fontFamily: palette.fontFamilies.poppins,
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '16px',
    whiteSpace: 'nowrap'
  },
  /** Approved allocations use the same green the utility rail marks them with. */
  statusChipDone: {
    color: palette.success.dark,
    backgroundColor: palette.success.light
  },
  statusChipWarning: {
    color: palette.warning.dark,
    backgroundColor: palette.warning.light
  },
  confirmText: {
    color: palette.shades[800],
    fontFamily: palette.fontFamilies.poppins,
    fontSize: '15px',
    lineHeight: '24px'
  },
  summary: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '12px 16px',
    borderBottom: border
  },
  matchBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px',
    borderRadius: '4px',
    backgroundColor: palette.success.light,
    '& .MuiSvgIcon-root': {
      flexShrink: 0,
      marginTop: '2px',
      width: '24px',
      height: '24px',
      fontSize: '24px'
    }
  },
  matchBoxWarning: {
    backgroundColor: palette.warning.light
  },
  matchTitle: {
    color: palette.success.dark,
    fontFamily: palette.fontFamilies.poppins,
    fontSize: '13px',
    fontWeight: 600
  },
  matchText: {
    color: palette.success.dark,
    fontFamily: palette.fontFamilies.source,
    fontSize: '14px',
    lineHeight: 1.2
  },
  matchTitleWarning: {
    color: palette.warning.dark
  },
  matchTextWarning: {
    color: palette.warning.dark
  },
  sectionLabel: {
    color: palette.shades[800],
    fontFamily: palette.fontFamilies.poppins,
    fontSize: '13px',
    fontWeight: 600
  },
  summaryList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '2px',
    color: palette.shades[800],
    fontFamily: palette.fontFamilies.source,
    fontSize: '14px',
    lineHeight: 1.4
  },
  summaryLabel: {
    flexShrink: 0,
    width: '120px'
  },
  /** Custom-field labels are the bill's own words, so they need more room than the other rows. */
  summaryLabelWide: {
    flexShrink: 0,
    width: '150px'
  },
  /**
   * For a row whose label is a phrase rather than a word. The two-column shape the rest of the summary
   * uses gives the label a fixed 150px, and anything longer wraps to three lines inside it while its
   * value sits alongside the first — which reads as the two interleaved. This stacks them instead.
   */
  summaryRowStacked: {
    flexDirection: 'column',
    gap: '2px'
  },
  summaryLabelStacked: {
    width: 'auto'
  },
  summaryValue: {
    flex: 1,
    minWidth: 0,
    fontWeight: 600,
    wordBreak: 'break-word'
  },
  glSetupLink: {
    color: palette.primary.main,
    fontWeight: 600,
    whiteSpace: 'nowrap'
  },
  accordion: {
    backgroundColor: palette.shades[100]
  },
  accordionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    width: '100%',
    padding: '8px 16px',
    border: 'none',
    borderBottom: border,
    cursor: 'pointer',
    textAlign: 'left',
    backgroundColor: 'transparent',
    '& .MuiSvgIcon-root': {
      flexShrink: 0,
      width: '24px',
      height: '24px',
      fontSize: '24px',
      transition: palette.transitions.short
    }
  },
  accordionHeaderTitle: _objectSpread(_objectSpread({}, tableText), {}, {
    flex: 1,
    minWidth: 0,
    fontWeight: 600
  }),
  accordionArrowOpen: {
    '& .MuiSvgIcon-root': {
      transform: 'rotate(180deg)'
    }
  },
  accordionBody: {
    display: 'flex',
    alignItems: 'stretch',
    borderBottom: border
  },
  utilityRail: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    borderRight: border
  },
  utilityRailItem: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: '12px',
    border: 'none',
    borderRight: "3px solid ".concat(palette.shades[300]),
    cursor: 'pointer',
    backgroundColor: 'transparent'
  },
  utilityRailItemActive: {
    borderRightColor: palette.primary.main,
    backgroundColor: palette.shades[250]
  },
  utilityRailIconBox: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
    borderRadius: '2px',
    backgroundColor: palette.shades[300],
    '& .MuiSvgIcon-root': {
      width: '20px',
      height: '20px',
      fontSize: '20px'
    }
  },
  utilityRailIconBoxDone: {
    backgroundColor: palette.success.light
  },
  utilityRailStatus: {
    position: 'absolute',
    right: '7px',
    bottom: '8px',
    display: 'flex',
    padding: '2px',
    width: '12px',
    height: '12px',
    borderRadius: '999px',
    backgroundColor: palette.shades[200]
  },
  utilityRailStatusDot: {
    flex: 1,
    borderRadius: '99px',
    backgroundColor: palette.shades[500],
    boxShadow: '0px 18px 32px rgba(0, 0, 0, 0.15)'
  },
  utilityRailStatusDotDone: {
    backgroundColor: palette.success.main
  },
  /** Marks the utility whose service dates are already billed, so it is visible without opening it. */
  utilityRailStatusDotWarning: {
    backgroundColor: palette.warning.main
  },
  utilityPanel: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: '12px'
  },
  lineItemsHead: _objectSpread(_objectSpread({}, tableText), {}, {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderBottom: border,
    fontWeight: 600
  }),
  lineItemsBody: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0 16px 12px',
    borderBottom: border
  },
  lineItemRow: _objectSpread(_objectSpread({}, tableText), {}, {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
    borderBottom: border,
    '&:last-child': {
      borderBottom: 'none'
    }
  }),
  lineItemName: {
    flex: 1,
    minWidth: 0,
    wordBreak: 'break-word'
  },
  lineItemGlCode: {
    flex: 1,
    minWidth: 0,
    wordBreak: 'break-word'
  },
  /** Line item GL codes are read from the summary, so they are shown greyed even while editing. */
  lineItemReadOnly: {
    color: palette.shades[700]
  },
  lineItemAmount: {
    flexShrink: 0,
    width: '100px',
    fontWeight: 600,
    textAlign: 'right'
  },
  lineItemTotalLabel: {
    flex: 1,
    minWidth: 0,
    fontFamily: palette.fontFamilies.poppins,
    fontWeight: 600
  },
  lineItemTotalAmount: {
    flexShrink: 0,
    width: '100px',
    fontFamily: palette.fontFamilies.poppins,
    fontWeight: 600,
    textAlign: 'right'
  },
  usageRow: _objectSpread(_objectSpread({}, tableText), {}, {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '12px 16px',
    whiteSpace: 'nowrap'
  }),
  usageValue: {
    fontWeight: 600
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
    padding: '12px 16px'
  },
  /**
   * The push messages sit beside the button here rather than under the amounts, so the top margin they
   * carry for that other placement would drop them below the control they belong to.
   */
  cardActionsMessages: {
    marginTop: 0
  },
  emptyState: _objectSpread(_objectSpread({}, tableText), {}, {
    padding: '12px 16px',
    color: palette.shades[700]
  }),
  /* Edit mode (Figma: "Edit bill details" and the allocation table edit state). */
  editSectionRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: border,
    color: palette.shades[800],
    fontFamily: palette.fontFamilies.poppins,
    fontSize: '13px',
    fontWeight: 600
  },
  editTable: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  editRow: {
    display: 'flex',
    alignItems: 'stretch',
    width: '100%'
  },
  editCellLabel: {
    flex: '1 0 0',
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    minHeight: '44px',
    padding: '8px 8px 8px 16px',
    borderBottom: border,
    borderRight: border,
    color: palette.shades[800],
    fontFamily: palette.fontFamilies.source,
    fontSize: '15px',
    wordBreak: 'break-word'
  },
  editCellValue: {
    flex: '1 0 0',
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minHeight: '44px',
    padding: '6px 8px 6px 16px',
    borderBottom: border,
    borderRight: border
  },
  editCellAction: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '44px',
    padding: '6px',
    borderBottom: border,
    backgroundColor: palette.shades[200]
  },
  editInput: {
    width: '100%',
    color: palette.shades[800],
    fontFamily: palette.fontFamilies.source,
    fontSize: '15px',
    '& input': {
      height: 'auto',
      padding: 0
    }
  },
  /** The shared date picker ships an outlined field; inside a table cell the cell is the outline. */
  editDateField: {
    width: '100%',
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '& .MuiOutlinedInput-root': {
      paddingRight: '0 !important'
    },
    '& .MuiOutlinedInput-input': {
      padding: 0,
      color: palette.shades[800],
      fontFamily: palette.fontFamilies.source,
      fontSize: '15px'
    },
    '& .MuiSvgIcon-root': {
      width: '20px',
      height: '20px',
      fontSize: '20px'
    }
  },
  /** A cell the bill states rather than asks for: same type as the inputs, without the affordance. */
  editStaticValue: {
    flex: 1,
    minWidth: 0,
    color: palette.shades[800],
    fontFamily: palette.fontFamilies.source,
    fontSize: '15px',
    wordBreak: 'break-word'
  },
  /** The shared range picker, stripped to the cell: the row already names it and draws its border. */
  editRangeField: {
    width: '100%',
    '& .MuiFormLabel-root': {
      display: 'none'
    },
    // The picker sizes itself to its text, which would leave the calendar icon mid-cell.
    '& > div, & .react-datepicker-wrapper, & .react-datepicker__input-container': {
      display: 'block',
      width: '100%'
    },
    '& .MuiFormControl-root': {
      width: '100%',
      minWidth: 0
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '& .MuiOutlinedInput-root': {
      paddingRight: '0 !important'
    },
    '& .MuiOutlinedInput-input': {
      padding: 0,
      color: palette.shades[800],
      fontFamily: palette.fontFamilies.source,
      fontSize: '15px'
    },
    '& .MuiSvgIcon-root': {
      width: '20px',
      height: '20px',
      fontSize: '20px'
    }
  },
  editChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    flex: '1 0 0',
    minWidth: 0
  },
  editChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    maxWidth: '100%',
    padding: '2px 4px 2px 8px',
    borderRadius: '2px',
    backgroundColor: palette.shades[250],
    color: palette.shades[800],
    fontFamily: palette.fontFamilies.source,
    fontSize: '13px'
  },
  editChipLabel: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  },
  editChipRemove: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: 0,
    border: 'none',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    '& .MuiSvgIcon-root': {
      width: '16px',
      height: '16px',
      fontSize: '16px'
    }
  },
  editIconButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px',
    border: 'none',
    borderRadius: '2px',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    '& .MuiSvgIcon-root': {
      width: '16px',
      height: '16px',
      fontSize: '16px'
    },
    '&:hover': {
      backgroundColor: palette.shades[300]
    },
    /** A value the bill states keeps the button, so the column reads the same all the way down. */
    '&:disabled': {
      opacity: 0.4,
      cursor: 'default'
    },
    '&:disabled:hover': {
      backgroundColor: 'transparent'
    }
  },
  /** Holds the trash column open on rows that have no delete button, so the amounts stay aligned. */
  editIconSpacer: {
    flexShrink: 0,
    width: '28px'
  },
  editAddRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: border
  },
  editFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
    padding: '12px 16px'
  },
  editHint: _objectSpread(_objectSpread({}, tableText), {}, {
    padding: '0 16px 12px',
    color: palette.shades[700]
  }),
  lineItemEditName: {
    flex: '1 0 0',
    minWidth: 0
  },
  lineItemEditAmount: {
    flexShrink: 0,
    width: '110px',
    '& input': {
      textAlign: 'right'
    }
  },
  lineItemAdornment: {
    color: palette.shades[700],
    fontFamily: palette.fontFamilies.source,
    fontSize: '13px'
  },
  /** Shown next to a value the user can override, so the extracted figure stays visible. */
  deductionValue: {
    color: palette.shades[800],
    fontFamily: palette.fontFamilies.poppins,
    fontSize: '13px',
    fontWeight: 600
  },
  /* The edit trail (LB-5840): who changed what, newest first. */
  editHistoryEntry: {
    padding: '12px 16px',
    borderBottom: border
  },
  editHistorySummary: _objectSpread(_objectSpread({}, tableText), {}, {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 600
  }),
  editHistoryWhen: {
    color: palette.shades[700],
    fontWeight: 400
  },
  editHistoryChange: _objectSpread(_objectSpread({}, tableText), {}, {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    paddingTop: '8px'
  }),
  editHistoryChangeLabel: {
    flexShrink: 0,
    minWidth: '140px',
    color: palette.shades[700]
  },
  editHistoryValues: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '6px',
    minWidth: 0,
    flexWrap: 'wrap'
  },
  /** What it was, kept visible beside what it is now rather than replaced by it. */
  editHistoryOldValue: {
    color: palette.shades[700],
    textDecoration: 'line-through'
  },
  editHistoryNewValue: {
    color: palette.shades[800],
    fontWeight: 600
  },
  editHistoryMore: _objectSpread(_objectSpread({}, tableText), {}, {
    width: '100%',
    padding: '10px 16px',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    fontWeight: 600,
    color: palette.primary.main,
    backgroundColor: 'transparent'
  })
});
export default useStyles;